import React from "react";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";
import RefreshIcon from "@material-ui/icons/Refresh";
import withShipment from "../../../withShipment";
import TopToolbar from "../../global/subcomponents/topToolbar/TopToolbar";
import { listLedgerAudit } from "../../../redux/actions/accounting";
import { ledgerAuditSelector, loadingSelector } from "../../../redux/selectors/accounting";
import "./LedgerAudit.css";

class LedgerAudit extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            // when set, a single card's detail screen is shown instead of the overview
            detail: null,
            // all sections start collapsed — expand on click
            expanded: {
                warnings: false,
                categoryToLedgers: false,
                transactionCategories: false,
                subledgers: false,
                predefinedLedgers: false,
                summaryFieldReference: false,
            },
        };
    }

    componentDidMount() {
        this.props.listLedgerAudit();
    }

    toggleSection = (key) => {
        this.setState((prev) => ({
            expanded: { ...prev.expanded, [key]: !prev.expanded[key] },
        }));
    };

    openDetail = (key) => this.setState({ detail: key });

    closeDetail = () => this.setState({ detail: null });

    // maps a clickable card key -> the focused detail screen (title + table)
    detailConfig = (audit) => {
        const cats = audit.transactionCategories || [];
        const renderCats = (rows) =>
            rows.length ? this.renderTransactionCategories(rows)
                : <div className="audit-empty">No categories in this group.</div>;
        return {
            allLedgers: {
                title: "All ledgers",
                render: () => this.renderAllLedgers(audit.allLedgers || []),
            },
            transactionSourceLedgers: {
                title: "Transaction-sourced ledgers and their categories",
                render: () => this.renderCategoryToLedgers(audit.categoryToLedgers || []),
            },
            subLedgerParents: {
                title: "Sub-ledgers",
                render: () => this.renderSubledgers(audit.subledgers || []),
            },
            predefinedLedgers: {
                title: "Predefined ledgers",
                render: () => this.renderPredefinedLedgers(audit.predefinedLedgers || []),
            },
            transactionCategories: {
                title: "All transaction categories",
                render: () => renderCats(cats),
            },
            unusedConfig: {
                title: "Unused categories — not connected to any ledger",
                render: () => renderCats(cats.filter((c) => c.isUnusedConfig)),
            },
            unusedData: {
                title: "Unused categories — no transactions",
                render: () => renderCats(cats.filter((c) => c.isUnusedData)),
            },
            warnings: {
                title: "Warnings & integrity issues",
                render: () => this.renderWarnings(audit.warnings),
            },
        };
    };

    renderSection = (key, title, count, badgeType, renderContent) => {
        const isOpen = this.state.expanded[key];
        return (
            <div className="audit-section" key={key}>
                <div className="audit-section-header" onClick={() => this.toggleSection(key)}>
                    <span className="audit-section-arrow">{isOpen ? "▾" : "▸"}</span>
                    <span className="audit-section-title">{title}</span>
                    {count != null && (
                        <span className={`audit-badge audit-badge-${badgeType || "neutral"}`}>{count}</span>
                    )}
                </div>
                {isOpen && <div className="audit-section-body">{renderContent()}</div>}
            </div>
        );
    };

    renderTotals = (totals) => {
        if (!totals) return null;
        // cards with a `key` are clickable and drill into a detail screen (see detailConfig)
        const cards = [
            { key: "allLedgers", label: "Total Ledgers", value: totals.ledgers },
            { key: "transactionSourceLedgers", label: "Transaction-sourced Ledgers", value: totals.transactionSourceLedgers },
            { key: "subLedgerParents", label: "Roll-up Ledgers (with sub-ledgers)", value: totals.subLedgerParents },
            { key: "predefinedLedgers", label: "Predefined Ledgers", value: totals.predefinedLedgers },
            { key: "transactionCategories", label: "Transaction Categories", value: totals.transactionCategories },
            { key: "unusedConfig", label: "Categories Not in Any Ledger", value: totals.unusedCategoriesConfig, warn: totals.unusedCategoriesConfig > 0 },
            { key: "warnings", label: "Integrity Warnings", value: totals.warnings, warn: totals.warnings > 0 },
        ];
        return (
            <div className="audit-totals">
                {cards.map((c) => (
                    <div
                        className={`audit-total-card ${c.warn ? "audit-total-warn" : ""} ${c.key ? "audit-total-clickable" : ""}`}
                        key={c.label}
                        onClick={c.key ? () => this.openDetail(c.key) : undefined}
                    >
                        <div className="audit-total-value">{c.value}</div>
                        <div className="audit-total-label">{c.label}</div>
                    </div>
                ))}
            </div>
        );
    };

    renderAllLedgers = (rows) => (
        <table className="audit-table">
            <thead>
                <tr>
                    <th>Ledger</th>
                    <th>Type</th>
                    <th>Source</th>
                    <th>Sum Rule</th>
                    <th># Categories</th>
                    <th>Flags</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((r) => (
                    <tr key={r.ledgerId}>
                        <td>{r.name}</td>
                        <td>{r.type || <span className="audit-muted">—</span>}</td>
                        <td>{r.source || <span className="audit-muted">—</span>}</td>
                        <td>{r.sumRule || <span className="audit-muted">—</span>}</td>
                        <td>{r.categoryCount}</td>
                        <td>
                            {r.isPredefined && <span className="audit-badge audit-badge-neutral">predefined</span>}
                            {r.isShowTrialBalance && <span className="audit-badge audit-badge-ok">trial balance</span>}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    renderCategoryToLedgers = (rows) => (
        <table className="audit-table">
            <thead>
                <tr>
                    <th>Ledger</th>
                    <th>Type</th>
                    <th>Sum Rule (field picked)</th>
                    <th>Transaction Categories</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((r) => (
                    <tr key={r.ledgerId}>
                        <td>{r.name}</td>
                        <td>{r.type}</td>
                        <td>{r.sumRule || <span className="audit-muted">—</span>}</td>
                        <td>
                            {(r.categories || []).length
                                ? r.categories.map((c) => <span className="audit-chip" key={c}>{c}</span>)
                                : <span className="audit-muted">no categories</span>}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    renderTransactionCategories = (rows) => (
        <table className="audit-table">
            <thead>
                <tr>
                    <th>Category</th>
                    <th>Status</th>
                    <th>Used by ledgers</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((r) => (
                    <tr key={r.name}>
                        <td>{r.name}</td>
                        <td>
                            {r.isUnusedConfig && <span className="audit-badge audit-badge-warn">Not in any ledger</span>}
                            {r.isUnusedData && <span className="audit-badge audit-badge-neutral">No transactions</span>}
                            {!r.isUnusedConfig && !r.isUnusedData && <span className="audit-badge audit-badge-ok">Used</span>}
                        </td>
                        <td>
                            {(r.referencedByLedgers || []).length
                                ? r.referencedByLedgers.map((l) => <span className="audit-chip" key={l}>{l}</span>)
                                : <span className="audit-muted">—</span>}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    renderSubledgers = (rows) => {
        if (!rows.length) return <div className="audit-empty">No ledgers use other ledgers as sub-ledgers.</div>;
        return (
            <table className="audit-table">
                <thead>
                    <tr>
                        <th>Parent Ledger</th>
                        <th>Type</th>
                        <th>Sub-ledgers (children)</th>
                        <th>Issues</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.ledgerId}>
                            <td>{r.name}</td>
                            <td>{r.type}</td>
                            <td>
                                {(r.children || []).length
                                    ? r.children.map((c) => <span className="audit-chip" key={c.name}>{c.name}</span>)
                                    : <span className="audit-muted">—</span>}
                            </td>
                            <td>
                                {(r.unresolvedNames || []).map((n) => (
                                    <span className="audit-badge audit-badge-warn" key={`u-${n}`}>unresolved: {n}</span>
                                ))}
                                {(r.ambiguousNames || []).map((a) => (
                                    <span className="audit-badge audit-badge-warn" key={`a-${a.name}`}>ambiguous: {a.name} ({a.count} duplicate ledgers)</span>
                                ))}
                                {!(r.unresolvedNames || []).length && !(r.ambiguousNames || []).length && (
                                    <span className="audit-muted">—</span>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    renderPredefinedLedgers = (rows) => (
        <table className="audit-table">
            <thead>
                <tr>
                    <th>Predefined Ledger</th>
                    <th>Type</th>
                    <th>Populated summaries</th>
                    <th>Source services / fields</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((r) => (
                    <tr key={r.ledgerId}>
                        <td>{r.name}</td>
                        <td>{r.type}</td>
                        <td>
                            {r.hasData
                                ? r.populatedSummaries.map((s) => <span className="audit-chip" key={s}>{s}</span>)
                                : <span className="audit-badge audit-badge-warn">no data</span>}
                        </td>
                        <td>
                            {(r.fieldReference || []).length
                                ? r.fieldReference.map((f) => (
                                    <div className="audit-ref-line" key={f.summaryType}>
                                        <strong>{f.label}:</strong> {f.sourceService} ← {f.sourceDomain} — {f.fieldsPicked}
                                    </div>
                                ))
                                : <span className="audit-muted">—</span>}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    renderSummaryFieldReference = (rows) => (
        <table className="audit-table">
            <thead>
                <tr>
                    <th>Summary</th>
                    <th>Source Service</th>
                    <th>Source Domain</th>
                    <th>Field Picked</th>
                    <th>Consumed By</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((r) => (
                    <tr key={r.summaryType}>
                        <td>{r.label}</td>
                        <td>{r.sourceService}</td>
                        <td>{r.sourceDomain}</td>
                        <td>{r.fieldsPicked}</td>
                        <td>{r.consumedBy}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    renderWarnings = (warnings) => {
        if (!warnings) return null;
        const blocks = [];

        if ((warnings.ledgersWithBrokenConnections || []).length) {
            blocks.push(
                <div className="audit-warn-block" key="broken">
                    <div className="audit-warn-title">Ledgers with broken connections (source is set, but a connected category or sub-ledger no longer exists — removed or renamed)</div>
                    {warnings.ledgersWithBrokenConnections.map((l) => (
                        <div className="audit-warn-item" key={l.ledgerId}>
                            <strong>{l.name}</strong> ({l.source}) → missing: {l.brokenReferences.join(", ")}
                        </div>
                    ))}
                </div>
            );
        }
        if ((warnings.emptyCategoryLedgers || []).length) {
            blocks.push(
                <div className="audit-warn-block" key="empty">
                    <div className="audit-warn-title">Ledgers with no categories (will never pull data)</div>
                    {warnings.emptyCategoryLedgers.map((l) => (
                        <div className="audit-warn-item" key={l.ledgerId}><strong>{l.name}</strong> (source: {l.source})</div>
                    ))}
                </div>
            );
        }
        if ((warnings.multiLedgerCategories || []).length) {
            blocks.push(
                <div className="audit-warn-block" key="multi">
                    <div className="audit-warn-title">Categories used by more than one ledger (double-counting risk)</div>
                    {warnings.multiLedgerCategories.map((m) => (
                        <div className="audit-warn-item" key={m.category}>“{m.category}” → {m.ledgers.join(", ")}</div>
                    ))}
                </div>
            );
        }
        const unc = warnings.uncategorizedTransactions;
        if (unc > 0) {
            blocks.push(
                <div className="audit-warn-block" key="uncat">
                    <div className="audit-warn-title">Uncategorized transactions (empty category or not matching any category)</div>
                    <div className="audit-warn-item">{unc} transaction(s)</div>
                </div>
            );
        }
        if ((warnings.predefinedLedgersWithoutData || []).length) {
            blocks.push(
                <div className="audit-warn-block" key="pred">
                    <div className="audit-warn-title">Predefined ledgers with no data loaded (any year / currency)</div>
                    {warnings.predefinedLedgersWithoutData.map((l) => (
                        <div className="audit-warn-item" key={l.ledgerId}><strong>{l.name}</strong></div>
                    ))}
                </div>
            );
        }

        if (!blocks.length) return <div className="audit-empty">No issues found. Everything is wired up.</div>;
        return <div className="audit-warnings">{blocks}</div>;
    };

    render() {
        const audit = this.props.ledgerAudit;
        const detail = audit && this.state.detail ? this.detailConfig(audit)[this.state.detail] : null;
        // In a detail screen the toolbar shows that screen's title + a back-to-audit icon.
        // On the overview it shows the audit title + back-to-ledgers and refresh.
        const menuItems = detail
            ? [
                {
                    title: "Back to Audit",
                    icon: <KeyboardBackspaceIcon />,
                    onClick: this.closeDetail,
                },
            ]
            : [
                {
                    title: "Back to Ledgers",
                    icon: <KeyboardBackspaceIcon />,
                    onClick: () => this.props.onBack && this.props.onBack(),
                },
                {
                    title: "Refresh",
                    icon: <RefreshIcon />,
                    onClick: () => this.props.listLedgerAudit(),
                },
            ];
        return (
            <React.Fragment>
                <TopToolbar
                    pageName={detail ? detail.title : "Ledger Audit"}
                    menuItems={menuItems}
                />

                <div className="audit-page">
                    {!audit && this.props.loading && <div className="audit-empty">Loading audit…</div>}
                    {!audit && !this.props.loading && <div className="audit-empty">No audit data.</div>}

                    {audit && detail && (
                        <div className="audit-detail-body">
                            {detail.render()}
                        </div>
                    )}

                    {audit && !detail && (
                    <React.Fragment>
                        <p className="audit-intro">
                            Read-only view of how the ledger module is wired: which transaction categories feed
                            which ledger, which categories are defined but unused, which ledgers roll up as
                            sub-ledgers, and where predefined ledgers draw their numbers from.
                            Click any card below to drill into its details.
                        </p>
                        {this.renderTotals(audit.totals)}
                        {this.renderSection("warnings", "Warnings & Integrity Issues", audit.totals?.warnings, "warn",
                            () => this.renderWarnings(audit.warnings))}
                        {this.renderSection("categoryToLedgers", "Category → Ledger Connections", (audit.categoryToLedgers || []).length, "neutral",
                            () => this.renderCategoryToLedgers(audit.categoryToLedgers || []))}
                        {this.renderSection("transactionCategories", "Transaction Categories", (audit.transactionCategories || []).length, "neutral",
                            () => this.renderTransactionCategories(audit.transactionCategories || []))}
                        {this.renderSection("subledgers", "Sub-ledgers", (audit.subledgers || []).length, "neutral",
                            () => this.renderSubledgers(audit.subledgers || []))}
                        {this.renderSection("predefinedLedgers", "Predefined Ledgers", (audit.predefinedLedgers || []).length, "neutral",
                            () => this.renderPredefinedLedgers(audit.predefinedLedgers || []))}
                        {this.renderSection("summaryFieldReference", "Predefined Summary Field Reference", (audit.summaryFieldReference || []).length, "neutral",
                            () => this.renderSummaryFieldReference(audit.summaryFieldReference || []))}
                    </React.Fragment>
                    )}
                </div>
            </React.Fragment>
        );
    }
}

const mapStateToProps = (state) => ({
    ledgerAudit: ledgerAuditSelector(state),
    loading: loadingSelector(state),
});

const actionCreators = {
    listLedgerAudit,
};

export default withShipment({
    actionCreators,
    mapStateToProps,
}, LedgerAudit);
